import { groupBy, meanBy, minBy } from 'es-toolkit';

import { LumberSpecies } from '@paragon/contract-library';
import { sortBy, throwIfNull } from '@paragon/utilities';

/**
 * Maintained serverside in LumberSpeciesRankings.BaseRankedSpecies
 */
const baseRankedSpecies: readonly LumberSpecies[] = [
  'BC_OSB',
  'Yellow_Poplar',
  'Yellow_Cedar',
  'White_Oak',
  'Western_Woods',
  'Western_Cedars',
  'Spruce_Pine_Fir_South',
  'Spruce_Pine_Fir',
  'Southern_Pine',
  'Redwood',
  'Red_Oak',
  'Red_Maple',
  'Northern_White_Cedar',
  'Northern_Species',
  'Northern_Red_Oak',
  'Mixed_Southern_Pine',
  'Mixed_Oak',
  'Mixed_Maple',
  'Hem_Fir_North',
  'Hem_Fir',
  'Engelmann_Spruce_Lodgepole_Pine',
  'Eastern_White_Pine',
  'Eastern_Softwoods',
  'Eastern_Hemlock_Tamarack',
  'Eastern_Hemlock_Balsam_Fir',
  'Douglas_Fir_South',
  'Douglas_Fir_Larch_North',
  'Douglas_Fir_Larch',
  'Cottonwood',
  'Coast_Sitka_Spruce',
  'Beech_Birch_Hickory',
  'Bald_Cypress',
  'Aspen',
  'Alaska_Yellow_Cedar',
  'Alaska_Spruce',
  'Alaska_Hemlock',
  'Alaska_Cedar',
  'KSLVL',
  'LP_SolidStart_LSL',
  'LP_LVL',
  'MasterPlank_LVL',
  'RigidLam_LVL',
];

const tangibleSpeciesBaseRankMap = new Map<LumberSpecies, number>(
  baseRankedSpecies.map((species, rank) => [species, rank]),
);

function getTangibleSpeciesBaseRank(tangibleSpecies: LumberSpecies) {
  const tangibleSpeciesRank = tangibleSpeciesBaseRankMap.get(tangibleSpecies);
  if (tangibleSpeciesRank == null) throw new Error(`Tangible species ${tangibleSpecies} not found in base rankings.`);
  return tangibleSpeciesRank;
}

/**
 * Maintained serverside in the LumberSpeciesRankings constructor
 */
export function buildRankingsIncludingComboSpecies(
  comboSpeciesList: readonly (readonly LumberSpecies[])[],
): readonly (readonly LumberSpecies[])[] {
  const comboSpeciesByLowestTangibleSpecies = groupBy(
    comboSpeciesList.map((comboSpecies) => comboSpecies.toSorted()),
    (comboSpecies) => throwIfNull(minBy(comboSpecies, getTangibleSpeciesBaseRank)),
  );
  return baseRankedSpecies.flatMap((baseSpecies) => [
    // Combo lumber is ranked just below its lowest tangible species because it is preferable based on
    // its cost flexibility. If needed, rank it against other combo species.
    ...sortBy(
      comboSpeciesByLowestTangibleSpecies[baseSpecies] ?? [],
      // A higher option count always ranks lower (better) than a lower option count if they share the
      // same lowest tangible species.
      [(comboSpecies) => comboSpecies.length, (comboSpecies) => meanBy(comboSpecies, getTangibleSpeciesBaseRank)],
      ['desc', 'asc'],
    ),
    [baseSpecies],
  ]);
}

/**
 * Maintained serverside in LumberSpeciesRankings.Get
 */
export function getSpeciesRank(
  lumberSpecies: LumberSpecies,
  tangibleSpecies: readonly LumberSpecies[],
  rankings: readonly (readonly LumberSpecies[])[],
): number {
  // Compared as a whole sequence, as LumberSpeciesRankings.Get does with SequenceEqual. Comparing element by element
  // alone matches on a prefix, so a combo these rankings were not built with would quietly take the rank of a shorter
  // entry its species happen to start with, usually the lone entry for its first species. Better to say it is absent.
  const speciesToRank = lumberSpecies === 'Combo' ? tangibleSpecies.toSorted() : [lumberSpecies];
  const index = rankings.findIndex(
    (rankedSpeciesEntry) =>
      rankedSpeciesEntry.length === speciesToRank.length &&
      rankedSpeciesEntry.every((species, i) => species === speciesToRank[i]),
  );
  if (index === -1) throw new Error(`${lumberSpecies} not found in ranking`);
  return index;
}
